import { useEffect, useState } from "react";
import { Building2, Save } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

type CompanyForm = {
  legalName: string;
  tradeName: string;
  regCode: string;
  vatCode: string;
  addressLine: string;
  city: string;
  postalCode: string;
  country: string;
  email: string;
  phone: string;
  website: string;
  bankName: string;
  bankAccount: string;
  swiftCode: string;
  signatoryName: string;
  signatoryTitle: string;
  signatoryEmail: string;
};

const EMPTY_FORM: CompanyForm = {
  legalName: "",
  tradeName: "",
  regCode: "",
  vatCode: "",
  addressLine: "",
  city: "",
  postalCode: "",
  country: "LT",
  email: "",
  phone: "",
  website: "",
  bankName: "",
  bankAccount: "",
  swiftCode: "",
  signatoryName: "",
  signatoryTitle: "",
  signatoryEmail: "",
};

function nullable(v: string): string | null {
  const t = v.trim();
  return t.length === 0 ? null : t;
}

export function SettingsCompanyPage() {
  const utils = trpc.useUtils();
  const data = trpc.companyDetails.get.useQuery();
  const [form, setForm] = useState<CompanyForm>(EMPTY_FORM);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (loaded || !data.data) return;
    const c = data.data;
    setForm({
      legalName: c.legalName ?? "",
      tradeName: c.tradeName ?? "",
      regCode: c.regCode ?? "",
      vatCode: c.vatCode ?? "",
      addressLine: c.addressLine ?? "",
      city: c.city ?? "",
      postalCode: c.postalCode ?? "",
      country: c.country ?? "LT",
      email: c.email ?? "",
      phone: c.phone ?? "",
      website: c.website ?? "",
      bankName: c.bankName ?? "",
      bankAccount: c.bankAccount ?? "",
      swiftCode: c.swiftCode ?? "",
      signatoryName: c.signatoryName ?? "",
      signatoryTitle: c.signatoryTitle ?? "",
      signatoryEmail: c.signatoryEmail ?? "",
    });
    setLoaded(true);
  }, [data.data, loaded]);

  const update = trpc.companyDetails.update.useMutation({
    onSuccess: () => {
      utils.companyDetails.get.invalidate();
      toast.success("Įmonės duomenys išsaugoti");
    },
    onError: (e) => toast.error(e.message ?? "Išsaugoti nepavyko"),
  });

  const setField = (key: keyof CompanyForm) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    update.mutate({
      legalName: nullable(form.legalName),
      tradeName: nullable(form.tradeName),
      regCode: nullable(form.regCode),
      vatCode: nullable(form.vatCode),
      addressLine: nullable(form.addressLine),
      city: nullable(form.city),
      postalCode: nullable(form.postalCode),
      country: nullable(form.country),
      email: nullable(form.email),
      phone: nullable(form.phone),
      website: nullable(form.website),
      bankName: nullable(form.bankName),
      bankAccount: nullable(form.bankAccount),
      swiftCode: nullable(form.swiftCode),
      signatoryName: nullable(form.signatoryName),
      signatoryTitle: nullable(form.signatoryTitle),
      signatoryEmail: nullable(form.signatoryEmail),
    });
  };

  if (data.isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6">
      <div className="mb-6 flex items-center gap-3">
        <Building2 className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Įmonės duomenys</h1>
          <p className="text-sm text-muted-foreground">
            Šie duomenys naudojami sutarties šablonuose kaip <code>{"{company.*}"}</code>{" "}
            kintamieji.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pagrindinė informacija</CardTitle>
            <CardDescription>Naudojama sutartyse ir sąskaitose.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Juridinis pavadinimas</Label>
              <Input value={form.legalName} onChange={setField("legalName")} className="mt-1" placeholder="UAB Noium" />
            </div>
            <div className="sm:col-span-2">
              <Label>Prekės ženklas (neprivaloma)</Label>
              <Input value={form.tradeName} onChange={setField("tradeName")} className="mt-1" placeholder="Noium" />
            </div>
            <div>
              <Label>Įmonės kodas</Label>
              <Input value={form.regCode} onChange={setField("regCode")} className="mt-1" placeholder="123456789" />
            </div>
            <div>
              <Label>PVM kodas</Label>
              <Input value={form.vatCode} onChange={setField("vatCode")} className="mt-1" placeholder="LT123456789" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Adresas</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Gatvė</Label>
              <Input value={form.addressLine} onChange={setField("addressLine")} className="mt-1" placeholder="Gedimino pr. 1" />
            </div>
            <div>
              <Label>Miestas</Label>
              <Input value={form.city} onChange={setField("city")} className="mt-1" />
            </div>
            <div>
              <Label>Pašto kodas</Label>
              <Input value={form.postalCode} onChange={setField("postalCode")} className="mt-1" placeholder="LT-01103" />
            </div>
            <div>
              <Label>Šalis</Label>
              <Input value={form.country} onChange={setField("country")} className="mt-1" placeholder="LT" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kontaktai</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>El. paštas</Label>
              <Input type="email" value={form.email} onChange={setField("email")} className="mt-1" />
            </div>
            <div>
              <Label>Telefonas</Label>
              <Input value={form.phone} onChange={setField("phone")} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label>Internetinis puslapis</Label>
              <Input value={form.website} onChange={setField("website")} className="mt-1" placeholder="https://noium.com" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Bankas</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label>Banko pavadinimas</Label>
              <Input value={form.bankName} onChange={setField("bankName")} className="mt-1" />
            </div>
            <div className="sm:col-span-2">
              <Label>Sąskaita (IBAN)</Label>
              <Input value={form.bankAccount} onChange={setField("bankAccount")} className="mt-1" placeholder="LT00 0000 0000 0000 0000" />
            </div>
            <div>
              <Label>SWIFT/BIC</Label>
              <Input value={form.swiftCode} onChange={setField("swiftCode")} className="mt-1" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pasirašantis asmuo</CardTitle>
            <CardDescription>
              Naudojamas sutarties šablonuose kaip{" "}
              <code>{"{company.signatory_name}"}</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label>Vardas, pavardė</Label>
              <Input value={form.signatoryName} onChange={setField("signatoryName")} className="mt-1" />
            </div>
            <div>
              <Label>Pareigos</Label>
              <Input value={form.signatoryTitle} onChange={setField("signatoryTitle")} className="mt-1" placeholder="Direktorius" />
            </div>
            <div className="sm:col-span-2">
              <Label>El. paštas</Label>
              <Input type="email" value={form.signatoryEmail} onChange={setField("signatoryEmail")} className="mt-1" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={update.isPending}>
            <Save className="h-4 w-4" />
            {update.isPending ? "Saugoma…" : "Išsaugoti"}
          </Button>
        </div>
      </form>
    </div>
  );
}
